import React, { useRef } from "react";
import Icon from "../utils/icons";
import { money, discountPercent, PLACEHOLDER_IMG, sizedImage } from "../utils/formatters";
import { needsPick } from "../utils/variants";

// The home page's Offers section: a deals strip in the store's colour with large deal cards. Each card shows the
// discount, the owner's optional offer title and description (website editor -> Offers), how much the shopper
// saves, and add to cart / quantity.
function DealCard({ p, add, onSelect, cart, updateQty }) {
    // Sized products are added through the size picker (utils/variants.js), so they keep the deal button
    const quantity = needsPick(p) ? 0 : (cart.find((item) => item.id === p.id) || {}).qty || 0;
    const inStock = p.inStock !== false;
    const discount = discountPercent(p);
    const saving = discount > 0 ? p.originalPrice - p.price : 0;
    const stop = (event) => event.stopPropagation();

    return (
        <article className="dealCard" onClick={() => onSelect && onSelect(p)} tabIndex={0} role="button"
            onKeyDown={(event) => { if (event.key === "Enter") onSelect && onSelect(p); }}>
            <div className="dealPic">
                <img src={p.img ? sizedImage(p.img, 600) : PLACEHOLDER_IMG} alt={p.name} loading="lazy" decoding="async"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }} />
                {discount > 0 && <span className="dealRibbon"><b>{discount}%</b> OFF</span>}
                <span className="dealTag"><Icon.Tag aria-hidden="true" /> {p.offerTitle || "Special offer"}</span>
            </div>
            <div className="dealBody">
                <h3>{p.name}</h3>
                {Array.isArray(p.comboItems) && p.comboItems.length > 0 && (
                    <p className="dealIncludes">Includes {p.comboItems.map((i) => `${i.qty > 1 ? `${i.qty}× ` : ""}${i.name}`).join(" + ")}</p>
                )}
                {p.offerNote && <p className="dealNote">{p.offerNote}</p>}
                <div className="dealPrice">
                    <strong>{money(p.price)}</strong>
                    {discount > 0 && <del>{money(p.originalPrice)}</del>}
                </div>
                {saving > 0 && <span className="dealSave">You save {money(saving)}</span>}
                {!inStock ? (
                    <button type="button" className="dealBtn isOut" disabled onClick={stop}>Out of stock</button>
                ) : quantity > 0 ? (
                    <div className="dealQty" onClick={stop} role="group" aria-label={`Quantity for ${p.name}`}>
                        <button type="button" aria-label={`Decrease ${p.name} quantity`} onClick={() => updateQty(p.id, quantity - 1)}>−</button>
                        <span aria-live="polite">{quantity}</span>
                        <button type="button" aria-label={`Increase ${p.name} quantity`} onClick={() => updateQty(p.id, quantity + 1)}>+</button>
                    </div>
                ) : (
                    <button type="button" className="dealBtn" onClick={(event) => { stop(event); add(p); }}>
                        <Icon.Bag aria-hidden="true" /> Grab the deal
                    </button>
                )}
            </div>
        </article>
    );
}

export default function OfferDeals({ title, items, add, onSelect, cart = [], updateQty, viewAll }) {
    const track = useRef(null);
    const best = Math.max(0, ...items.map((p) => discountPercent(p)));
    const scroll = (direction) => {
        const el = track.current;
        if (el) el.scrollBy({ left: direction * Math.max(260, el.clientWidth * 0.8), behavior: "smooth" });
    };

    return (
        <section className="offerDeals" aria-label={title}>
            <div className="offerDealsHead">
                <div>
                    <span className="offerDealsEyebrow"><Icon.Fire aria-hidden="true" /> Limited-time deals</span>
                    <h2>{title}</h2>
                    <p>{best > 0 ? `Save up to ${best}% on ${items.length} hand-picked product${items.length === 1 ? "" : "s"}` : `${items.length} hand-picked product${items.length === 1 ? "" : "s"}`}</p>
                </div>
                <div className="offerDealsActions">
                    <button type="button" className="offerDealsArrow" aria-label="Previous deals" onClick={() => scroll(-1)}><Icon.ChevronLeft /></button>
                    <button type="button" className="offerDealsArrow" aria-label="More deals" onClick={() => scroll(1)}><Icon.ChevronRight /></button>
                    <button type="button" className="offerDealsAll" onClick={viewAll}>View all <Icon.ArrowRight aria-hidden="true" /></button>
                </div>
            </div>
            <div className="offerDealsTrack" ref={track}>
                {items.map((p) => <DealCard key={p.id} p={p} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} />)}
            </div>
        </section>
    );
}
